import moment from "moment";
import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, Bell } from "lucide-react";
import Logo from "../../assets/appImage/Logo.png";
import { onLogout } from "../../../redux/slices/user.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import NotificationApi from "../../../utils/api/notification/notification.api";
import { Notification } from "../../../utils/api/notification/notification.type";
import { isCommercant, isInvestisseur } from "../../routes/renderRoutes";
import { Routes, getRoutePath } from "../../routes/routes";
import VersionChecker from "../../utils/VersionChecker";

import "./FrontHeader.css";

const FrontHeader = () => {
  const { user } = useAppSelector((s) => s.user);
  const isConnected = !!user?.id;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const is_investisseur = isInvestisseur(user);
  const listeProduit = useAppSelector((p) => p.produit);

  // Gestion du style au scroll pour l'effet "glassmorphism" dynamique
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`front-header ${isScrolled ? "scrolled" : ""}`}>
      <div className="front-header-inner">
        {/* Logo */}
        <Link to="/" className="logo">
          <div className="logo-mark" style={{ background: 'var(--ink)' }}>
            <img src={Logo} alt="Maajjal" style={{ padding: '6px', width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          {/* <small>v5.2.8</small> */}
        </Link>

        {/* Nav Links (Desktop) */}
        <div className="nav-links d-none d-lg-flex">
          <NavLink to="/" className="nav-link">
            Accueil
          </NavLink>
          <NavLink to="/app/campagnes" className="nav-link">
            Campagnes
          </NavLink>
          <NavLink to="/app/lancer-une-campagne" className="nav-link nav-link-primary">
            Lancer une campagne
          </NavLink>
          <NavLink to="/app/marketplace" className="nav-link">
            Marketplace
          </NavLink>
          <NavLink to="/app/qui-sommes-nous" className="nav-link">
            Qui-sommes-nous ?
          </NavLink>
        </div>

        {/* Right Actions */}
        <div className="nav-actions">
          {/* Panier */}
          <NavLink to="/app/mon-panier" className="icon-btn" title="Mon panier">
            <ShoppingBag size={20} />
            {listeProduit?.length > 0 && (
              <span className="badge-count">{listeProduit?.length}</span>
            )}
          </NavLink>

          <Notifications />

          {!isConnected ? (
            <NavLink to="/se-connecter" className="btn-primary d-none d-sm-inline-flex">
              Connexion
            </NavLink>
          ) : (
            <div className="dropdown">
              <button
                className="btn-primary dropdown-toggle"
                type="button"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="dropdown-btn-logged-user-name">
                  {user?.first_name} {user?.last_name}
                </span>
              </button>

              <ul
                className="dropdown-menu dropdown-menu-end shadow-sm border-0 mt-2"
                aria-labelledby="dropdownMenuButton"
                style={{ borderRadius: "12px", minWidth: "200px" }}
              >
                <DropDownItem
                  title="Tableau de bord"
                  route={
                    is_investisseur
                      ? "ListeProjets"
                      : isCommercant(user)
                        ? "MerchantDashboard"
                        : "DashboardAdmin"
                  }
                />
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li className="text-center p-2">
                  <button
                    onClick={() => {
                      dispatch(onLogout());
                      navigate(getRoutePath("home"));
                    }}
                    className="btn btn-sm btn-danger w-100"
                    style={{ borderRadius: "999px" }}
                  >
                    Déconnexion
                  </button>
                </li>
              </ul>
            </div>
          )}

          {/* Mobile menu toggle */}
          <button
            className="icon-btn d-lg-none"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#mobileMenuContent"
            aria-controls="mobileMenuContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu Collapse */}
      <div className="collapse d-lg-none border-top" id="mobileMenuContent">
        <div className="d-flex flex-column p-3 nv">
          <NavLink to="/" className="nav-link p-2">
            Accueil
          </NavLink>
          <NavLink to="/app/campagnes" className="nav-link p-2">
            Campagnes
          </NavLink>
          <NavLink to="/app/lancer-une-campagne" className="nav-link nav-link-primary p-2">
            Lancer une campagne
          </NavLink>
          <NavLink to="/app/marketplace" className="nav-link p-2">
            Marketplace
          </NavLink>
          <NavLink to="/app/qui-sommes-nous" className="nav-link p-2">
            Qui-sommes-nous ?
          </NavLink>
          {!isConnected && (
            <NavLink to="/se-connecter" className="btn-primary mt-3 justify-content-center d-sm-none">
              Connexion
            </NavLink>
          )}
        </div>
      </div>
    </nav>
  );
};
export default FrontHeader;

type MenuLinkItemProps = {
  route: Routes;
  img?: any;
  title: string;
  arr?: any;
};

export function DropDownItem({ route, title, arr }: MenuLinkItemProps) {
  return (
    <li>
      <NavLink
        className="dropdown-item py-2"
        style={{ fontSize: "14px", borderRadius: "8px", margin: "0 8px", width: "auto" }}
        to={getRoutePath(route)}
      >
        {title}{" "}
        {title === "Notifications" && (
          <span className="badge bg-info text-dark ms-2"> {arr?.length}</span>
        )}
        {title === "Messages" && (
          <span className="badge bg-info text-dark ms-2">
            {" "}
            {/* {numberMessages(arr)}{" "} */}
          </span>
        )}
      </NavLink>
    </li>
  );
}

function Notifications() {
  const [data, count, onShow, onClick] = useNotifications();

  return (
    <div className="dropdown position-relative">
      <button
        className="icon-btn dropdown-toggle-hide-arrow"
        type="button"
        id="dropdownNotifButton"
        data-bs-toggle="dropdown"
        onClick={() => onShow()}
        aria-expanded="false"
        title="Notifications"
      >
        <Bell size={20} />
        {count > 0 && <span className="notif-dot"></span>}
      </button>
      <ul
        style={{ width: 280, borderRadius: "12px", padding: "8px" }}
        className="dropdown-menu dropdown-menu-end shadow-sm border-0 mt-2"
        aria-labelledby="dropdownNotifButton"
      >
        {data.length === 0 && (
          <li
            className="dropdown-item text-muted text-center py-3"
            style={{ fontSize: 13, borderRadius: "8px" }}
          >
            Pas de notification
          </li>
        )}
        {data.map((item) => (
          <li
            style={{
              whiteSpace: "initial",
              fontSize: 13,
              borderRadius: "8px",
              marginBottom: "4px",
            }}
            className="dropdown-item cursor-pointer py-2"
            onClick={() => onClick(item)}
            key={item.id}
          >
            {item.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function useNotifications(): [
  Notification[],
  number,
  () => any,
  (item: Notification) => any
] {
  const navigate = useNavigate();
  const [update] = NotificationApi.useUpdateMutation();
  const [count, setCount] = useState(0);
  const { data = [] } = NotificationApi.useGetQuery();

  useEffect(() => {
    setCount(data.filter((f) => !f.read).length);
  }, [data]);

  const user = useAppSelector((s) => s.user.user);

  const onClick = (item: Notification) => {
    if (user?.user_type !== "admin") {
      if (item.achat) {
        navigate(
          getRoutePath(user?.is_double_type ? "CommandeList" : "MesCommandes")
        );
      }
    } else {
      if (item.achat) {
        navigate(getRoutePath("CommandesAdmin"));
      }
    }
  };

  const onShow = () => {
    for (let i of data) {
      if (!i.read) {
        update({ read: true, id: i.id });
      }
    }
  };

  const monthAfter = moment().add(-1, "months").toDate();
  return [
    data.filter((p) => !p.read || moment(p.date_created).toDate() > monthAfter),
    count,
    onShow,
    onClick,
  ];
}